import React, { useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Avatar, IconButton, Chip, Grid,
    TextField, MenuItem, Switch, Button, Alert, CircularProgress,
} from "@mui/material";
import EventNoteIcon from "@mui/icons-material/EventNote";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import StorefrontIcon from "@mui/icons-material/Storefront";

import { LOYALTY_API_BASE, SECTION_COLORS } from "../../constants";
import { useBrand } from "../../../../contexts/BrandContext";

const ORIGINS = ["custom", "system", "external"];

const INITIAL_FORM = {
    key: "",
    origin: "custom",
    name: "",
    description: "",
    payload_schema: "",   // JSON string
    active: true,
};

const CreateEventTypeDialog = ({ open, onClose, onCreated }) => {
    const { currentBrand } = useBrand();
    const brandName = currentBrand?.name || "";

    const [form, setForm] = useState({ ...INITIAL_FORM });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const handle = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));
    const isValid = form.key.trim() && form.origin.trim() && form.name.trim();

    const handleSubmit = async () => {
        if (!isValid) return;
        setSubmitting(true);
        setError(null);
        try {
            let payloadSchema = null;
            if (form.payload_schema.trim()) {
                try { payloadSchema = JSON.parse(form.payload_schema); }
                catch { throw new Error("payload_schema n'est pas un JSON valide"); }
            }
            const body = {
                brand: brandName || null,
                key: form.key.trim(),
                origin: form.origin.trim(),
                name: form.name.trim(),
                description: form.description.trim() || undefined,
                payload_schema: payloadSchema,
                active: form.active,
            };
            const res = await fetch(`${LOYALTY_API_BASE}/admin/event-types`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Brand": brandName
                },
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const e = await res.json().catch(() => null);
                throw new Error(e?.detail || `Erreur HTTP ${res.status}`);
            }
            onCreated();
            handleClose();
        } catch (err) { setError(err.message); } finally { setSubmitting(false); }
    };

    const handleClose = () => { setForm({ ...INITIAL_FORM }); setError(null); onClose(); };

    const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };
    const monoFieldSx = { ...fieldSx, "& textarea": { fontFamily: "monospace", fontSize: 12 } };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ borderBottom: "2px solid #f1f3f5", px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, background: SECTION_COLORS.eventTypes.gradient }}>
                        <EventNoteIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Typography variant="h6" fontFamily="Montserrat, sans-serif" fontWeight={700} color="#1a1a2e" fontSize={16}>
                        Nouveau Type d'Événement
                    </Typography>
                </Box>
                <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "24px !important", px: 3, pb: 2 }}>
                {/* Brand badge */}
                <Box display="flex" alignItems="center" gap={1} mb={2.5} px={1.5} py={1}
                    sx={{ borderRadius: 2, bgcolor: "#f0f9ff", border: "1px solid #bae6fd" }}>
                    <StorefrontIcon sx={{ fontSize: 18, color: SECTION_COLORS.eventTypes.main }} />
                    <Typography fontSize={13} color="text.secondary">Marque :</Typography>
                    <Chip
                        label={brandName || "Globale (aucune marque)"}
                        size="small"
                        sx={{
                            fontWeight: 700, fontSize: 12,
                            bgcolor: `${SECTION_COLORS.eventTypes.main}15`,
                            color: SECTION_COLORS.eventTypes.main
                        }} />
                </Box>

                <Grid container spacing={2}>
                    {/* key */}
                    <Grid item xs={12} sm={7}>
                        <TextField fullWidth required label="Clé (key)" value={form.key}
                            onChange={handle("key")} size="small"
                            helperText="Identifiant technique unique, ex: purchase_completed"
                            sx={fieldSx} />
                    </Grid>
                    {/* origin */}
                    <Grid item xs={12} sm={5}>
                        <TextField fullWidth required select label="Origine" value={form.origin}
                            onChange={handle("origin")} size="small"
                            helperText="Source de l'événement"
                            sx={fieldSx}>
                            {ORIGINS.map((o) => (
                                <MenuItem key={o} value={o}>
                                    {o === "custom" ? "Custom (personnalisé)" :
                                        o === "system" ? "System (interne)" :
                                            "External (externe)"}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    {/* name */}
                    <Grid item xs={12}>
                        <TextField fullWidth required label="Nom affiché" value={form.name}
                            onChange={handle("name")} size="small" sx={fieldSx} />
                    </Grid>
                    {/* description */}
                    <Grid item xs={12}>
                        <TextField fullWidth label="Description" value={form.description}
                            onChange={handle("description")} size="small" multiline rows={2} sx={fieldSx} />
                    </Grid>

                    {/* active */}
                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" px={1.5} py={1}
                            sx={{ borderRadius: 2, bgcolor: "#fafbfd", border: "1px solid #e8ecf1" }}>
                            <Box>
                                <Typography fontSize={13} fontWeight={600} fontFamily="Montserrat, sans-serif">Activer</Typography>
                                <Typography fontSize={11} color="text.secondary">Disponible immédiatement</Typography>
                            </Box>
                            <Switch checked={form.active} onChange={() => setForm((p) => ({ ...p, active: !p.active }))}
                                sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": { color: SECTION_COLORS.eventTypes.main },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: SECTION_COLORS.eventTypes.main }
                                }} />
                        </Box>
                    </Grid>
                </Grid>

                {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0", gap: 1 }}>
                <Button onClick={handleClose} sx={{ color: "text.secondary", textTransform: "none" }}>Annuler</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={!isValid || submitting}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                    sx={{
                        background: SECTION_COLORS.eventTypes.gradient, textTransform: "none", borderRadius: 2, fontWeight: 600, px: 3,
                        "&:hover": { boxShadow: `0 4px 16px ${SECTION_COLORS.eventTypes.main}40` }
                    }}>
                    {submitting ? "Création..." : "Créer"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateEventTypeDialog;
